// Ported from yaqatw's src/service/crypto/cipher.ts — pure Web Crypto (PBKDF2 -> AES-GCM),
// no dependencies. Kept near-verbatim so the two apps stay compatible.
export class Cipher {
  // Versioned header format: [version (1) | iterations (4) | salt (16) | iv (12) | ciphertext]
  private static readonly VERSION = 1;
  private static readonly ITERATIONS = 150_000;
  private static readonly SALT_LENGTH = 16; // bytes
  private static readonly IV_LENGTH = 12; // bytes (AES-GCM recommended)
  private static readonly HEADER_LENGTH = 1 + 4 + this.SALT_LENGTH + this.IV_LENGTH; // 33 bytes

  private static readonly enc = new TextEncoder();
  private static readonly dec = new TextDecoder();

  /* =========================
     Public API
  ========================== */

  static async encrypt(plaintext: string, password: string): Promise<Uint8Array> {
    const salt = crypto.getRandomValues(new Uint8Array(this.SALT_LENGTH));
    const iv = crypto.getRandomValues(new Uint8Array(this.IV_LENGTH));

    const key = await this.deriveKey(password, salt, this.ITERATIONS);

    const ciphertext = await crypto.subtle.encrypt(
      { name: "AES-GCM", iv }, // Uint8Array is OK here
      key,
      this.enc.encode(plaintext)
    );

    // Build header: [version (1) | iterations (4) | salt | iv]
    const header = this.buildHeader(this.VERSION, this.ITERATIONS, salt, iv);

    // Payload format: [header | ciphertext]
    return this.concat(header, new Uint8Array(ciphertext));
  }

  static async decrypt(encrypted: Uint8Array, password: string): Promise<string> {
    // Validate payload size
    if (encrypted.length < this.HEADER_LENGTH) {
      throw new Error(
        `Invalid encrypted payload: expected at least ${this.HEADER_LENGTH} bytes, got ${encrypted.length}`
      );
    }

    // Parse header: [version (1) | iterations (4) | salt (16) | iv (12)]
    const version = encrypted[0];

    if (version !== this.VERSION) {
      throw new Error(`Unsupported cipher version: ${version}`);
    }

    const iterations = this.readUint32(encrypted, 1);
    const salt = encrypted.slice(5, 5 + this.SALT_LENGTH);
    const iv = encrypted.slice(5 + this.SALT_LENGTH, 5 + this.SALT_LENGTH + this.IV_LENGTH);
    const data = encrypted.slice(5 + this.SALT_LENGTH + this.IV_LENGTH);

    const key = await this.deriveKey(password, salt, iterations);

    const decrypted = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv }, // Uint8Array
      key,
      this.toArrayBuffer(data) // guaranteed ArrayBuffer
    );

    return this.dec.decode(decrypted);
  }

  static async hash(password: string): Promise<string> {
    const encoded = this.enc.encode(password);
    const hashBuffer = await crypto.subtle.digest("SHA-256", encoded);
    return this.bufferToHex(hashBuffer);
  }

  static async compareHash(password: string, hash: string): Promise<boolean> {
    const newHash = await this.hash(password);
    return newHash === hash;
  }

  /**
   * Derive a master key from password (for caching in memory)
   * Uses a fixed salt specific to master key derivation
   * Returns extractable key that can be serialized
   */
  static async deriveMasterKey(
    password: string,
    serializable: boolean = false
  ): Promise<CryptoKey> {
    // Fixed salt for master key (derived from the string "MASTER_KEY_SALT")
    const fixedSalt = this.enc.encode("MASTER_KEY_SALT").slice(0, this.SALT_LENGTH);
    const paddedSalt = new Uint8Array(this.SALT_LENGTH);
    paddedSalt.set(fixedSalt);

    return this.deriveKey(password, paddedSalt, this.ITERATIONS, serializable);
  }

  /**
   * Export CryptoKey to JWK format (for storage)
   */
  static async exportKeyToJWK(key: CryptoKey): Promise<string> {
    const jwk = await crypto.subtle.exportKey("jwk", key);
    return JSON.stringify(jwk);
  }

  /**
   * Import CryptoKey from JWK format
   */
  static async importKeyFromJWK(jwkString: string): Promise<CryptoKey> {
    try {
      const jwk = JSON.parse(jwkString);
      return crypto.subtle.importKey("jwk", jwk, { name: "AES-GCM", length: 256 }, false, [
        "encrypt",
        "decrypt",
      ]);
    } catch (e) {
      throw new Error(`Failed to import key from JWK: ${e}`);
    }
  }

  /**
   * Encrypt with existing CryptoKey (no key derivation)
   * Uses fixed salt from master key derivation
   */
  static async encryptWithKey(plaintext: string, key: CryptoKey): Promise<Uint8Array> {
    const fixedSalt = this.enc.encode("MASTER_KEY_SALT").slice(0, this.SALT_LENGTH);
    const paddedSalt = new Uint8Array(this.SALT_LENGTH);
    paddedSalt.set(fixedSalt);

    const iv = crypto.getRandomValues(new Uint8Array(this.IV_LENGTH));

    const ciphertext = await crypto.subtle.encrypt(
      { name: "AES-GCM", iv },
      key,
      this.enc.encode(plaintext)
    );

    // Build header with fixed salt
    const header = this.buildHeader(this.VERSION, this.ITERATIONS, paddedSalt, iv);

    return this.concat(header, new Uint8Array(ciphertext));
  }

  /**
   * Decrypt with existing CryptoKey (no key derivation)
   */
  static async decryptWithKey(encrypted: Uint8Array, key: CryptoKey): Promise<string> {
    if (encrypted.length < this.HEADER_LENGTH) {
      throw new Error(
        `Invalid encrypted payload: expected at least ${this.HEADER_LENGTH} bytes, got ${encrypted.length}`
      );
    }

    // Parse header
    const version = encrypted[0];
    if (version !== this.VERSION) {
      throw new Error(`Unsupported cipher version: ${version}`);
    }

    const iv = encrypted.slice(5 + this.SALT_LENGTH, 5 + this.SALT_LENGTH + this.IV_LENGTH);
    const data = encrypted.slice(5 + this.SALT_LENGTH + this.IV_LENGTH);

    const decrypted = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv },
      key,
      this.toArrayBuffer(data)
    );

    return this.dec.decode(decrypted);
  }

  /* =========================
     Header Utilities
  ========================== */

  /**
   * Build header: [version (1) | iterations (4) | salt (16) | iv (12)]
   */
  private static buildHeader(
    version: number,
    iterations: number,
    salt: Uint8Array,
    iv: Uint8Array
  ): Uint8Array {
    const header = new Uint8Array(1 + 4 + salt.length + iv.length);
    let offset = 0;

    // Version (1 byte)
    header[offset++] = version;

    // Iterations (4 bytes, big-endian uint32)
    header[offset++] = (iterations >>> 24) & 0xff;
    header[offset++] = (iterations >>> 16) & 0xff;
    header[offset++] = (iterations >>> 8) & 0xff;
    header[offset++] = iterations & 0xff;

    // Salt
    header.set(salt, offset);
    offset += salt.length;

    // IV
    header.set(iv, offset);

    return header;
  }

  /**
   * Read uint32 (big-endian) from byte array at offset
   */
  private static readUint32(data: Uint8Array, offset: number): number {
    return (
      (data[offset] << 24) | (data[offset + 1] << 16) | (data[offset + 2] << 8) | data[offset + 3]
    );
  }

  /* =========================
     Key Derivation
  ========================== */

  private static async deriveKey(
    password: string,
    salt: Uint8Array,
    iterations: number = this.ITERATIONS,
    extractable: boolean = false
  ): Promise<CryptoKey> {
    const baseKey = await crypto.subtle.importKey(
      "raw",
      this.enc.encode(password),
      "PBKDF2",
      false,
      ["deriveKey"]
    );

    return crypto.subtle.deriveKey(
      {
        name: "PBKDF2",
        salt: this.toArrayBuffer(salt), // guaranteed ArrayBuffer
        iterations: iterations,
        hash: "SHA-256",
      },
      baseKey,
      { name: "AES-GCM", length: 256 },
      extractable,
      ["encrypt", "decrypt"]
    );
  }

  /* =========================
     Utilities
  ========================== */

  /**
   * Forces a real ArrayBuffer (never SharedArrayBuffer)
   * Required for WebCrypto + strict TypeScript
   */
  private static toArrayBuffer(data: Uint8Array): ArrayBuffer {
    const buffer = new ArrayBuffer(data.byteLength);
    new Uint8Array(buffer).set(data);
    return buffer;
  }

  private static concat(...arrays: Uint8Array[]): Uint8Array {
    const totalLength = arrays.reduce((sum, a) => sum + a.length, 0);
    const result = new Uint8Array(totalLength);

    let offset = 0;
    for (const arr of arrays) {
      result.set(arr, offset);
      offset += arr.length;
    }

    return result;
  }

  private static bufferToHex(buffer: ArrayBuffer): string {
    const hashArray = Array.from(new Uint8Array(buffer));
    return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
  }
}

export default Cipher;
